/**
 * A case's key facts: one TanStack query shared by the Key Facts tab and the
 * workspace tree's badge, patched live from `/api/events` (the Case IDE's case
 * scope carries `key_fact.updated`, `key_fact.removed` and `key_facts.state`)
 * and refetched when the agent's tools invalidate `key_facts`.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { queryClient } from "@/lib/query/queryClient";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { useOnCaseInvalidate } from "../../caseIde/context/caseInvalidation";
import { KeyFactsAPI } from "../api";
import {
  applyState,
  removeFact,
  upsertFact,
  type KeyFactsStateEvent
} from "../model";
import type { KeyFact, KeyFactsCase } from "../types";

export const keyFactsQueryKey = (individualId: string) =>
  ["key-facts", individualId] as const;

export type KeyFactsState = {
  data: KeyFactsCase | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Put a key fact an action returned into the cache before its event lands. */
  put: (fact: KeyFact) => void;
};

export const useKeyFacts = (individualId: string | null): KeyFactsState => {
  const id = individualId ?? "";
  const key = keyFactsQueryKey(id);
  const query = useQuery(
    {
      queryKey: key,
      queryFn: () => KeyFactsAPI.list(id),
      enabled: !!id
    },
    queryClient
  );

  const patch = useCallback(
    (fn: (data: KeyFactsCase) => KeyFactsCase) => {
      queryClient.setQueryData<KeyFactsCase>(keyFactsQueryKey(id), (data) =>
        data ? fn(data) : data
      );
    },
    [id]
  );

  useRealtimeEvent<{ individual_id: string; fact: KeyFact }>(
    "key_fact.updated",
    (data) => {
      if (data?.individual_id === id && data.fact)
        patch((d) => upsertFact(d, data.fact));
    }
  );
  useRealtimeEvent<{ individual_id: string; key: string }>(
    "key_fact.removed",
    (data) => {
      if (data?.individual_id === id) patch((d) => removeFact(d, data.key));
    }
  );
  useRealtimeEvent<KeyFactsStateEvent>("key_facts.state", (data) => {
    if (data?.individual_id === id) patch((d) => applyState(d, data));
  });

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: keyFactsQueryKey(id) });
  }, [id]);
  useOnCaseInvalidate(id, ["key_facts"], refetch);

  const put = useCallback(
    (fact: KeyFact) => patch((d) => upsertFact(d, fact)),
    [patch]
  );

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? (query.error as Error).message || "Couldn't load Case Insights"
      : null,
    refetch,
    put
  };
};
